import { map } from 'lodash/fp';
import { createAction } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { getSelectedEntityId } from '../../entityManagement/selectors';
import { DELETE_ENTITY_SUCCEEDED, stopEntityDeleting } from '../../entityManagement/actions';
import {
    getSelectedCategoryIdFromMeta,
    getSelectedFloorId,
    getSelectedResource,
    getResource,
    getRecursiveChildrenResourceIds,
    getResourcesByIds,
} from '../selectors/resources';
import { getCategoryById } from '../selectors/categories';
import { generateUnique32Integer } from '../utils';
import {
    RESOURCES_DELETED,
    RESOURCES_UPDATED,
    RESOURCE_DESELECTED,
    RESOURCE_SELECTED,
    RESOURCES_DESELECTED,
} from './types';
import { splitLines, createResourceStub } from './helpers';

interface CreateResourcesProps {
    categoryId: string;
    floorId: string;
    resourceNames: string[];
    resourceCategoryIndexes: number[];
}

export const addResources =
    ({ result }: { result: string }): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const categoryId = getSelectedCategoryIdFromMeta(state);
        const floorId = getSelectedFloorId(state);

        const resourceNames = splitLines(result);

        const { index } = getCategoryById(state, { id: categoryId });

        const resources = createResources({ resourceNames, floorId, categoryId, resourceCategoryIndexes: [index] });

        return dispatch(createAction(RESOURCES_UPDATED, resources));
    };

export const createResources = ({
    resourceNames,
    floorId,
    categoryId,
    resourceCategoryIndexes,
}: CreateResourcesProps) =>
    map((name: string) => {
        const indexCounter = generateUnique32Integer();
        return createResourceStub({ name, floorId, categoryId, index: indexCounter, resourceCategoryIndexes });
    })(resourceNames);

export const deleteResources = (ids: string[]) => createAction(RESOURCES_DELETED, ids);

export const addResource =
    ({ name, parentResourceBuilderId }: { name: string; parentResourceBuilderId: string }): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();

        const indexCounter = generateUnique32Integer();
        // @ts-expect-error
        const { index: parentResourceIndex } = parentResourceBuilderId
            ? getResource(state, { id: parentResourceBuilderId })
            : { index: null };
        const categoryId = getSelectedCategoryIdFromMeta(state);
        const floorId = getSelectedFloorId(state);

        const { index: resourceCategoryIndex } = getCategoryById(state, { id: categoryId });
        const resource = createResourceStub({
            name,
            floorId,
            categoryId,
            index: indexCounter,
            resourceCategoryIndexes: [resourceCategoryIndex],
            parentResourceIndex,
            parentResourceBuilderId,
        });

        dispatch(createAction(RESOURCES_UPDATED, resource));
    };

export const editResource =
    (
        { name, parentResourceBuilderId }: { name: string; parentResourceBuilderId: string },
        scope: string
    ): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        // @ts-expect-error
        const { index: parentResourceIndex } = getResource(state, { id: parentResourceBuilderId });
        const resource = getSelectedResource(state, { scope });

        void dispatch(
            createAction(RESOURCES_UPDATED, {
                ...resource,
                name,
                parentResourceIndex,
                parentResourceBuilderId,
            })
        );
    };

export const editResources =
    ({
        resourceIds,
        parentResourceBuilderId,
    }: {
        resourceIds: string[];
        parentResourceBuilderId: string;
    }): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();

        // @ts-expect-error
        const { index: parentResourceIndex } = getResource(state, { id: parentResourceBuilderId });
        const resources = getResourcesByIds(state, { resourceIds });

        const newResources = map(
            (resource) => ({
                // @ts-expect-error
                ...resource,
                parentResourceIndex,
                parentResourceBuilderId,
            }),
            resources
        );

        dispatch(createAction(RESOURCES_UPDATED, newResources));
    };

export const deleteResource =
    (__: unknown, scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const resourceId = getSelectedEntityId(getState(), { scope });

        dispatch(stopEntityDeleting(scope));
        dispatch(deleteResourcesByIds([resourceId]));
        dispatch(createAction(DELETE_ENTITY_SUCCEEDED, undefined, { scope }));
    };

export const deleteResourcesByIds =
    (ids: string[]): CommanderThunkAction =>
    (dispatch, getState) => {
        const childrenResourceIds = getRecursiveChildrenResourceIds(getState(), { parentResourceBuilderIds: ids });

        dispatch(deleteResources([...ids, ...childrenResourceIds]));
    };

export const selectResource = (resourceId: string) => createAction(RESOURCE_SELECTED, resourceId);
export const deselectResource = (resourceId: string) => createAction(RESOURCE_DESELECTED, resourceId);
export const deselectAllResources = () => createAction(RESOURCES_DESELECTED);
